import { assertNever } from '@deltasierra/shared';
import { makeStyles } from '@material-ui/core';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import TabletAndroidIcon from '@material-ui/icons/TabletAndroid';
import * as React from 'react';
import styled from 'styled-components';
import { EmbeddedHtml } from '../../EmbeddedHtml';
import { DSDialogProps, DSDialog, DSToggleButton, DSToggleButtonGroup } from '../../mui';
import { Translate } from '../../Translate';
import { DSButton } from '../Buttons';
import { Tooltip } from '../Poppers';

type DisplayMode = 'desktop' | 'mobile' | 'tablet';

const useFullscreenDialogStyles = makeStyles({
    container: {
        flexDirection: 'column',
    },
    paper: {
        flex: 1,
        transition: 'all 0.3s',
    },
});

const StyledContentWrapper = styled.div`
    flex: auto;
    padding: 8px 24px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    display: flex;
    flex-direction: column;
`;

const StyledActionsWrapper = styled.div`
    flex: 0 0 auto;
    padding: 8px 24px;

    display: flex;
    flex-direction: row;
    align-items: center;
`;

const StyledToolActionsWrapper = styled.div`
    flex: 1;
`;

const StyledEndActionsWrapper = styled.div``;

const StyledEmbeddedHtml = styled(EmbeddedHtml)`
    flex: 1 auto;

    border: 1px black dashed;
`;

const StyledIframe = styled.iframe`
    flex: 1 auto;

    border: 1px black dashed;
`;

export type HtmlPreviewDialogProps = DSDialogProps & {
    defaultDisplayMode?: DisplayMode;
    canDisableInteraction?: boolean;
} & ({ html: string; htmlSrc?: undefined } | { html?: undefined; htmlSrc: string });

export function HtmlPreviewDialog({
    canDisableInteraction = false,
    children,
    defaultDisplayMode = 'tablet',
    html,
    htmlSrc,
    ...props
}: HtmlPreviewDialogProps): JSX.Element {
    const classes = useFullscreenDialogStyles();
    const [displayMode, setDisplayMode] = React.useState<DisplayMode>(defaultDisplayMode);
    const [interactionDisabled, setInteractionDisabled] = React.useState(true);

    let maxWidth: Exclude<DSDialogProps['maxWidth'], false | undefined>;

    switch (displayMode) {
        case 'desktop':
            maxWidth = 'xl';
            break;
        case 'tablet':
            maxWidth = 'md';
            break;
        case 'mobile':
            maxWidth = 'xs';
            break;
        default:
            maxWidth = assertNever(displayMode);
            break;
    }

    return (
        <DSDialog {...props} classes={classes} fullWidth maxWidth={maxWidth}>
            {children}
            <StyledContentWrapper>
                {htmlSrc && <StyledIframe src={htmlSrc} />}
                {html && <StyledEmbeddedHtml disableInteraction={interactionDisabled} html={html} />}
            </StyledContentWrapper>
            <StyledActionsWrapper>
                <StyledToolActionsWrapper>
                    <DSToggleButtonGroup>
                        <DSToggleButton selected={displayMode === 'desktop'} onClick={() => setDisplayMode('desktop')}>
                            <DesktopWindowsIcon />
                        </DSToggleButton>
                        <DSToggleButton selected={displayMode === 'tablet'} onClick={() => setDisplayMode('tablet')}>
                            <TabletAndroidIcon />
                        </DSToggleButton>
                        <DSToggleButton selected={displayMode === 'mobile'} onClick={() => setDisplayMode('mobile')}>
                            <PhoneAndroidIcon />
                        </DSToggleButton>
                        {canDisableInteraction && (
                            <Tooltip
                                tip={interactionDisabled ? 'Link interaction disabled' : 'Link interaction enabled'}
                            >
                                <DSToggleButton
                                    selected={interactionDisabled}
                                    onClick={() => setInteractionDisabled(!interactionDisabled)}
                                >
                                    {interactionDisabled ? <LinkOffIcon /> : <LinkIcon />}
                                </DSToggleButton>
                            </Tooltip>
                        )}
                    </DSToggleButtonGroup>
                </StyledToolActionsWrapper>
                <StyledEndActionsWrapper>
                    <DSButton autoFocus onClick={() => props.onClose?.({}, 'backdropClick')}>
                        <Translate keyId="COMMON.CLOSE" />
                    </DSButton>
                </StyledEndActionsWrapper>
            </StyledActionsWrapper>
        </DSDialog>
    );
}
HtmlPreviewDialog.displayName = 'HtmlPreviewDialog';
